import { useEffect } from 'react';
import { Clock, FileQuestion, Info, Minus, Plus } from 'lucide-react';
import { useWizard } from '../../../context/WizardContext';
import { NavigationButtons } from '../../../components/wizard/NavigationButtons';
import { DIFFICULTY_LEVELS } from '../../../types/wizard';
import type { DifficultyLevel } from '../../../types/wizard';
import { cn } from '../../../lib/utils';
import { useTranslation } from '../../../context/LanguageContext';
import { DIFFICULTY_DESCRIPTION_KEYS, DIFFICULTY_LABEL_KEYS } from '../../../i18n';
import { usePlanLimits } from '../../../hooks/usePlanLimits';

interface SpinnerFieldProps {
  label: string;
  value: number;
  unit: string;
  step: number;
  min: number;
  max?: number;
  onChange: (v: number) => void;
  icon: React.ComponentType<{ className?: string }>;
  hint?: string;
  notice?: React.ReactNode;
}

function SpinnerField({
  label,
  value,
  unit,
  step,
  min,
  max,
  onChange,
  icon: Icon,
  hint,
  notice,
}: Readonly<SpinnerFieldProps>) {
  const cap = (n: number) => (max === undefined ? n : Math.min(max, n));
  const atMax = max !== undefined && value >= max;

  return (
    <div className="space-y-2">
      <label className="flex items-center gap-1.5 text-sm font-semibold text-slate-300">
        <Icon className="h-4 w-4 text-brand-500" />
        {label}
      </label>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - step))}
          className="flex h-10 w-10 items-center justify-center rounded-xl border-2 border-slate-700 bg-slate-700 text-slate-300 hover:border-violet-500 hover:bg-violet-500/10 hover:text-violet-400 transition-all duration-150"
        >
          <Minus className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-2 rounded-xl border-2 border-slate-700 bg-slate-700 px-4 py-2 min-w-[130px] justify-center focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-500/20 transition-all">
          <input
            type="number"
            value={value}
            min={min}
            max={max}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (!Number.isNaN(n)) onChange(cap(n));
            }}
            className="w-16 text-center text-lg font-bold text-white focus:outline-none bg-transparent"
          />
          <span className="text-sm text-slate-400 font-medium">{unit}</span>
        </div>
        <button
          type="button"
          onClick={() => onChange(cap(value + step))}
          disabled={atMax}
          className="flex h-10 w-10 items-center justify-center rounded-xl border-2 border-slate-700 bg-slate-700 text-slate-300 hover:border-violet-500 hover:bg-violet-500/10 hover:text-violet-400 transition-all duration-150 disabled:pointer-events-none disabled:opacity-40"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
      {notice}
    </div>
  );
}

const DIFFICULTY_STYLES: Record<string, { selected: string; badge: string }> = {
  green: {
    selected: 'border-emerald-500/50 bg-emerald-500/10 shadow-glow',
    badge: 'text-emerald-300',
  },
  yellow: {
    selected: 'border-amber-500/50 bg-amber-500/10 shadow-glow',
    badge: 'text-amber-300',
  },
  red: {
    selected: 'border-rose-500/50 bg-rose-500/10 shadow-glow',
    badge: 'text-rose-300',
  },
  indigo: {
    selected: 'border-violet-500/50 bg-violet-500/10 shadow-glow',
    badge: 'text-violet-300',
  },
};

export function Step5Questions() {
  const { state, setNumberOfQuestions, setDuration, setDifficulty } = useWizard();
  const { t } = useTranslation();
  const limits = usePlanLimits();

  const isWritten = state.selectedExamType === 'written';
  const maxQuestions = ((): number | null => {
    if (!limits) return null;
    return isWritten ? limits.maxWritten : limits.maxMCQ;
  })();

  // The count is carried across steps, so switching exam type after picking a
  // number — MCQ 30, then back to written — can leave it above the new cap.
  // Pulling it down here keeps the field and the plan in agreement without the
  // user having to notice.
  useEffect(() => {
    if (maxQuestions !== null && state.numberOfQuestions > maxQuestions) {
      setNumberOfQuestions(maxQuestions);
    }
  }, [maxQuestions, state.numberOfQuestions, setNumberOfQuestions]);

  const atQuestionLimit = maxQuestions !== null && state.numberOfQuestions >= maxQuestions;
  const questionLimitNotice = maxQuestions !== null && (
    <p
      className={cn(
        'flex items-start gap-1.5 text-xs',
        atQuestionLimit ? 'text-amber-300' : 'text-slate-400'
      )}
    >
      <Info className="h-3.5 w-3.5 flex-shrink-0 mt-px" />
      {t(isWritten ? 'wizard.questionsLimitWritten' : 'wizard.questionsLimitMcq', {
        max: maxQuestions,
      })}
    </p>
  );

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold text-white">{t('wizard.questionsTitle')}</h2>
        <p className="text-sm text-slate-500 mt-1">{t('wizard.questionsSubtitle')}</p>
      </div>

      <div className="space-y-6">
        <SpinnerField
          label={t('wizard.questionsNumberLabel')}
          value={state.numberOfQuestions}
          unit={t('common.questions')}
          step={1}
          min={1}
          max={maxQuestions ?? undefined}
          onChange={setNumberOfQuestions}
          icon={FileQuestion}
          hint={isWritten ? t('wizard.questionsWrittenHint') : t('wizard.questionsMcqHint')}
          notice={questionLimitNotice}
        />

        <SpinnerField
          label={t('wizard.durationLabel')}
          value={state.duration}
          unit={t('common.minutesShort')}
          step={15}
          min={15}
          onChange={setDuration}
          icon={Clock}
          hint={t('wizard.durationHint')}
        />

        {/* Difficulty selector */}
        <div className="space-y-3">
          <p className="text-sm font-semibold text-slate-300">{t('wizard.difficultyLabel')}</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {DIFFICULTY_LEVELS.map((level) => {
              const isSelected = state.difficulty === level.id;
              const styles = DIFFICULTY_STYLES[level.color];
              return (
                <button
                  key={level.id}
                  type="button"
                  onClick={() => setDifficulty(level.id as DifficultyLevel)}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-2xl border-2 px-3 py-4 text-center transition-all duration-200',
                    isSelected
                      ? styles.selected
                      : 'border-slate-700 bg-slate-700/50 hover:border-slate-600 hover:-translate-y-0.5 hover:shadow-card-hover'
                  )}
                >
                  <span
                    className={cn('text-sm font-bold', isSelected ? styles.badge : 'text-white')}
                  >
                    {t(DIFFICULTY_LABEL_KEYS[level.id])}
                  </span>
                  <span
                    className={cn(
                      'text-[10px] mt-0.5',
                      isSelected ? styles.badge : 'text-slate-400'
                    )}
                  >
                    {t(DIFFICULTY_DESCRIPTION_KEYS[level.id])}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <NavigationButtons />
    </div>
  );
}
